import { useState, useEffect, useRef } from "react";
import api, { API_BASE } from "../../../utils/api";
import { toast } from "react-toastify";

const useBanner = () => {
  const [showForm, setShowForm] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;
  const [productSearch, setProductSearch] = useState("");
  const [productResults, setProductResults] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const isSelection = useRef(false);

  const emptyBannerData = {
    bannerImage: null,
    startDate: "",
    endDate: "",
    description: "",
    imagePath: "",
    visible: true,
    previewUrl: "",
    productId: null,
    productLabel: "",
    productImage: "",
  };

  const [bannerData, setBannerData] = useState(emptyBannerData);

  // Mobile detection
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch banners
  const fetchBanners = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/banners/banner`);
      setBanners(response.data);
    } catch (error) {
      console.error("Error fetching banners:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  // Product search with debounce
  useEffect(() => {
    if (isSelection.current) {
      isSelection.current = false;
      return;
    }
    if (!productSearch || productSearch.trim().length < 2) {
      setProductResults([]);
      return;
    }
    const delay = setTimeout(async () => {
      try {
        setLoadingProducts(true);
        const res = await api.get(`/products/search?query=${productSearch}`);
        setProductResults(res.data);
      } catch (err) {
        console.error("Product search failed", err);
      } finally {
        setLoadingProducts(false);
      }
    }, 400);
    return () => clearTimeout(delay);
  }, [productSearch]);

  const formatDateTimeLocal = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const handleProductInputChange = (value) => {
    setProductSearch(value);
    if (bannerData.productId) {
      setBannerData((prev) => ({ ...prev, productId: null, productLabel: "", productImage: "" }));
    }
    if (!value || value.trim().length < 2) {
      setProductResults([]);
    }
  };

  const handleSelectProduct = (product) => {
    isSelection.current = true;
    setBannerData((prev) => ({
      ...prev,
      productId: product._id,
      productLabel: product.productName,
      productImage: product.imageUrl || product.image,
    }));
    setProductSearch(product.productName);
    setProductResults([]);
  };

  const handleRowClick = (banner) => {
    setSelectedBanner(banner);
    setProductResults([]);
    setLoadingProducts(false);
    isSelection.current = true;
    setProductSearch("");

    // Open modal immediately with available data
    setBannerData({
      startDate: banner.startDate ? formatDateTimeLocal(banner.startDate) : "",
      endDate: banner.endDate ? formatDateTimeLocal(banner.endDate) : "",
      description: banner.description,
      imagePath: banner.imagePath || "",
      visible: banner.visible || false,
      bannerImage: null,
      previewUrl: banner.imagePath ? `${API_BASE}/${banner.imagePath}` : "",
      productId: banner.productId || null,
      productLabel: "",
      productImage: "",
    });
    setShowModal(true);

    // Fetch product label in background (non-blocking)
    if (banner.productId) {
      const productId =
        typeof banner.productId === "object" && banner.productId?._id
          ? banner.productId._id
          : banner.productId;
      api.get(`/products/${productId}`)
        .then((res) => {
          const product = res.data;
          isSelection.current = true;
          setProductSearch(product.productName || "");
          setBannerData((prev) => ({
            ...prev,
            productId: product._id || productId,
            productLabel: product.productName || "",
            productImage: product.imageUrl || product.image || "",
          }));
        })
        .catch((err) => console.error("Failed to load banner product details", err));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setBannerData((prev) => ({
      ...prev,
      bannerImage: file,
      imagePath: file ? "" : prev.imagePath,
      previewUrl: file ? URL.createObjectURL(file) : prev.previewUrl,
    }));
  };

  const handleChange = (field, value) => {
    setBannerData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!bannerData.bannerImage && !bannerData.imagePath) {
      toast.error("Please upload a banner image.");
      return;
    }
    if (!bannerData.startDate) {
      toast.error("Please select a start date and time.");
      return;
    }
    if (!bannerData.endDate) {
      toast.error("Please select an end date and time.");
      return;
    }

    const formData = new FormData();
    if (bannerData.bannerImage) formData.append("bannerImage", bannerData.bannerImage);
    formData.append("startDate", bannerData.startDate ? new Date(bannerData.startDate).toISOString() : "");
    formData.append("endDate", bannerData.endDate ? new Date(bannerData.endDate).toISOString() : "");
    formData.append("description", bannerData.description);
    formData.append("visible", bannerData.visible);
    formData.append("productId", bannerData.productId ?? "");

    try {
      if (selectedBanner) {
        await api.put(`/banners/${selectedBanner._id}`, formData);
        toast.success("Banner updated successfully!");
      } else {
        await api.post(`/banners/upload`, formData);
        toast.success("Banner uploaded successfully!");
      }
      setShowForm(false);
      setShowModal(false);
      fetchBanners();
    } catch (error) {
      console.error("Error saving banner:", error);
      toast.error("Failed to save banner. Please try again.");
    }
  };

  const handleToggleVisible = async (banner, e) => {
    e.stopPropagation(); // prevent row click / modal opening
    const newVisible = !banner.visible;
    // Optimistic update
    setBanners((prev) =>
      prev.map((b) => (b._id === banner._id ? { ...b, visible: newVisible } : b))
    );
    try {
      const formData = new FormData();
      formData.append("visible", newVisible);
      formData.append("startDate", banner.startDate ? new Date(banner.startDate).toISOString() : "");
      formData.append("endDate", banner.endDate ? new Date(banner.endDate).toISOString() : "");
      formData.append("description", banner.description || "");
      formData.append("productId", banner.productId ?? "");
      if (banner.imagePath) formData.append("imagePath", banner.imagePath);
      await api.put(`/banners/${banner._id}`, formData);
    } catch (error) {
      // Revert on failure
      setBanners((prev) =>
        prev.map((b) => (b._id === banner._id ? { ...b, visible: !newVisible } : b))
      );
    }
  };

  const handleClose = () => {
    setShowModal(false);
    setSelectedBanner(null);
    setProductSearch("");
    setProductResults([]);
    setBannerData(emptyBannerData);
  };

  const getStatus = (startDate, endDate, visible) => {
    if (visible === false) return "Not Active";
    const now = new Date();
    if (now < new Date(startDate)) return "Upcoming";
    if (now > new Date(endDate)) return "Expired";
    return "Active";
  };

  const dateTemplate = (date) =>
    date ? new Date(date).toLocaleString() : "N/A";

  const paginatedData = isMobile
    ? banners.slice((page - 1) * rowsPerPage, page * rowsPerPage)
    : banners;

  return {
    showForm, setShowForm,
    showModal,
    banners,
    loading,
    bannerData,
    selectedBanner,
    page, setPage,
    rowsPerPage,
    productSearch,
    productResults,
    loadingProducts,
    isMobile,
    paginatedData,
    handleProductInputChange,
    handleSelectProduct,
    handleRowClick,
    handleImageChange,
    handleChange,
    handleSubmit,
    handleToggleVisible,
    handleClose,
    getStatus,
    dateTemplate,
    API_BASE,
  };
};

export default useBanner;
